import { ApiClientReturnType, KeyLabelType, Nullable, TableDataType } from '~/types';
import api from '../http-common'
import axios, { CancelTokenSource } from 'axios';
import ENV from '~/environment'

// Registrations

const getRegistrations = (data): ApiClientReturnType<RegistratIndexType> =>
  api.post('/climate-program/registrations/index', data);

const editRegistration = (id) =>
  api.post(`/climate-program/registrations/${id}/edit`);

const showRegistration = (id) =>
  api.post(`/climate-program/registrations/${id}/show`);

const updateRegistration = ({ id, formData, onUploadProgress, cancelToken }) =>
  axios.post(`${ENV.API_URL}/climate-program/registrations/${id}/update`, formData, {
    headers: {
      'Authorization': localStorage.getItem('token') || '',
      'Content-Type': 'multipart/form-data'
    },
    onUploadProgress,
    cancelToken: cancelToken?.token
  })

// Purchases

const getPurchasesOptionsData = () =>
  api.post('/climate-program/purchases/get-options-data');

const getPurchasesIndexData = (): ApiClientReturnType<FeedProductsOptionsType> =>
  api.post('/climate-program/purchases/get-index-data');

const storePurchase = (data) =>
  api.post('/climate-program/purchases/store', data);

const getPurchases = (data): ApiClientReturnType<FeedPurchasesType> =>
  api.post('/climate-program/purchases/index', data);

const editPurchase = (id) =>
  api.post(`/climate-program/purchases/${id}/edit`);

const showPurchase = (id): ApiClientReturnType<FeedPurchaseShowType> =>
  api.post(`/climate-program/purchases/${id}/show`);

const deletePurchase = (id) =>
  api.post(`/climate-program/purchases/${id}/delete`);

const getFile = (id) =>
  api.post(`/climate-program/purchases/${id}/get-file`);

const getRegistrationFile = (id) =>
  api.post(`/climate-program/registrations/${id}/get-file`);

const createPurchase = ({ formData, onUploadProgress, cancelToken }: RegisterOptions) =>
  axios.post(`${ENV.API_URL}/climate-program/purchases/store`, formData, {
    headers: {
      'Authorization': localStorage.getItem('token') || '',
      'Content-Type': 'multipart/form-data'
    },
    onUploadProgress,
    cancelToken: cancelToken?.token
  })

const updatePurchase = ({ id, formData, onUploadProgress, cancelToken }: UpdatePurchaseOptions) =>
  axios.post(`${ENV.API_URL}/climate-program/purchases/${id}/update`, formData, {
    headers: {
      'Authorization': localStorage.getItem('token') || '',
      'Content-Type': 'multipart/form-data'
    },
    onUploadProgress,
    cancelToken: cancelToken?.token
  })

const registerClimaProgram = ({ formData, onUploadProgress, cancelToken }: RegisterOptions) =>
  axios.post(`${ENV.API_URL}/climate-program/register`, formData, {
    headers: {
      'Authorization': localStorage.getItem('token') || '',
      'Content-Type': 'multipart/form-data'
    },
    onUploadProgress,
    cancelToken: cancelToken?.token
  })

const closePurchase = (id) =>
  api.post(`/climate-program/purchases/${id}/close`)

const getClimaRegOptionsData = (): ApiClientReturnType<ClimaRegDataType> =>
  api.post('/climate-program/register/get-options-data')

const getAcceptTermsFile = (): ApiClientReturnType<PurchasOptionsType> =>
  api.post('/settings/climate-program/get-file')

const closeFeedPurchase = (id: string) =>
  api.post(`/climate-program/purchases/${id}/close`);

export const ClimaProgramService = {
  getRegistrations,
  editRegistration,
  updateRegistration,
  getPurchasesOptionsData,
  getPurchasesIndexData,
  storePurchase,
  getPurchases,
  editPurchase,
  showPurchase,
  updatePurchase,
  deletePurchase,
  showRegistration,
  getFile,
  closePurchase,
  registerClimaProgram,
  createPurchase,
  getClimaRegOptionsData,
  getRegistrationFile,
  getAcceptTermsFile,
  closeFeedPurchase
}

type RegistratIndexType = TableDataType<RegistrationType>

export interface RegistrationType {
  id: number;
  companyName: string;
  evsNumber: string;
  agisId: string;
  farmerId: string;
  year: number;
  streetName: string;
  streetNumber: string;
  zip: string;
  city: string;
  phoneNumber: null;
  email: null;
  memberSince: null;
  cowsNumber: number;
  avgMilkAmount: number;
  yearlyConcentratedFeedAmountPerCow: number;
  changesInLastThreeYears: number;
  selfCalculatedFeedingPlan: number;
  consultantFirstName: string;
  consultantLastName: string;
  consultantPhoneNumber: string;
  consultantEmail: string;
  consultantCompanyName: string;
  feedingPlanOriginalFilename: null;
  feedingPlanFilename: null;
  createdAt: Date;
  updatedAt: Date;
}

type FeedPurchasesType = TableDataType<FeedPurchaseType>
export interface FeedPurchaseType {
  id: number;
  companyName: string;
  evsNumber: string;
  agisId: string;
  farmerId: string;
  year: number;
  status: number;
  animalGroup: number;
  purchaseDate: Date;
  feedSupplierId: number;
  customerMix: number;
  feedProductId: null;
  agolineContent: string;
  purchasedFeedAmount: number;
  feedingFrom: Date;
  feedingTo: Date;
  purchaseReceiptOriginalFilename: string;
  purchaseReceiptFilename: string;
  createdAt: Date;
  updatedAt: Date;
  feedSupplierName: string;
  feedProductName: null;
  statusLabel: string;
  animalGroupLabel: string;
  agolineAmount: number;
  dairyCowsNumber: number;
  dryCowsNumber: number;
  agolinPerCowPerDay: number;
  dailyMineralIntakePerCow: number;
}

export interface FeedPurchaseShowType {
  evsNumber: string;
  companyName: string;
  id: number;
  farmerId: string;
  agisId: string;
  year: number;
  status: number;
  animalGroup: number;
  purchaseDate: Date;
  feedSupplierId: number;
  customerMix: number;
  feedProductId: null;
  agolineContent: string;
  purchasedFeedAmount: number;
  feedingFrom: Date;
  feedingTo: Date;
  purchaseReceiptOriginalFilename: string;
  purchaseReceiptFilename: string;
  createdAt: Date;
  updatedAt: Date;
  feedSupplierName: string;
  feedProductName: string;
  agolineAmount: string;
  dairyCowsDailyAgolineAmountPerCow: string;
  dryCowsDailyAgolineAmountPerCow: string;
  sumAnimalGroupsFeedAmount: string;
  climateProgramPurchaseId: number;
  dairyCowsDailyMineralIntakePerCow: string;
  dryCowsDailyMineralIntakePerCow: string;
  dairyCowsNumber: string;
  dryCowsNumber: string;
  statusLabel: string;
  animalGroupLabel: string;
}

export interface AnimalGroup {
  id: number;
  climateProgramPurchaseId: number;
  group: number;
  avgCowsNumberFedWithAgoline: number;
  dailyMineralIntakePerCow: number;
}

interface RegisterOptions {
  formData: FormData

  farmerId?: string
  onUploadProgress?: (progressEvent: { loaded: number; total?: number }) => void
  cancelToken?: CancelTokenSource
}

interface UpdatePurchaseOptions extends RegisterOptions {
  id?: string
}

export interface PurchasOptionsType {
  animalGroups: { key: number, label: string }[];
  feedSuppliers: FeedSupplier[];
}
export interface FeedSupplier {
  id: number;
  status: number;
  code: string;
  icon: string;
  name: string;
  label: string;
  feedSupplierId: number;
  languageId: number;
  products: Product[];
}

export interface Product {
  id: number;
  feedSupplierId: number;
  agolineContent: string;
  name: string;
  status: number;
}

export interface FeedProductsOptionsType {
  products: FeedProduct[];
  statuses: KeyLabelType[];
}

export interface FeedProduct {
  id: number | string;
  name: string;
}

export interface ClimaRegDataType {
  milkAmounts: Amount[];
  concentratedFeedsAmounts: Amount[];
  farmers: ClimaRegFarmerType[];
  email: string
}

export interface Amount {
  key: number;
  value: number;
}

export interface ClimaRegFarmerType {
  id: string;
  companyName: string;
  agisId: null;
  phoneNumber: null;
  addressableId: string;
  streetName: string;
  streetNumber: string;
  zip: string;
  climateProgramRegistration: Nullable<ClimaRegistrationDataType>
  city: string;
}

export interface ClimaRegistrationDataType {
  id: number;
  farmerId: string;
  year: number;
  companyName: string;
  agisId: string;
  streetName: string;
  streetNumber: string;
  zip: string;
  city: string;
  phoneNumber: null;
  email: null;
  memberSince: null;
  cowsNumber: number;
  avgMilkAmount: number;
  yearlyConcentratedFeedAmountPerCow: number;
  changesInLastThreeYears: number;
  selfCalculatedFeedingPlan: number;
  consultantFirstName: string;
  consultantLastName: string;
  consultantPhoneNumber: string;
  consultantEmail: string;
  consultantCompanyName: string;
  feedingPlanOriginalFilename: null;
  feedingPlanFilename: null;
  createdAt: Date;
  updatedAt: Date;
  changes: any[];
}

